import { useEffect, useRef, useState } from 'react';
import { resolveStorageUrl } from '../../lib/uploads/resolveStorageUrl';
import { previewPath } from '../../lib/products/publicProduct';

let activePlayer = null;
function stop(player) {
  if (!player) return;
  player.pause();
  try { player.currentTime = 0; } catch { /* Metadata may not be available yet. */ }
}
function formatTime(value) {
  const seconds = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  return Math.floor(seconds / 60) + ':' + String(seconds % 60).padStart(2, '0');
}
const EMPTY_MEDIA = { time: 0, duration: 0, playing: false, buffering: false, muted: false };

/** Branded controls use native audio playback and keyboard-accessible buttons/ranges. */
export function SoundPreview({ product }) {
  const path = previewPath(product);
  const container = useRef(null);
  const player = useRef(null);
  const [nearby, setNearby] = useState(typeof IntersectionObserver === 'undefined');
  const [state, setState] = useState({ path: '', url: null, loading: true, failed: false });
  const [media, setMedia] = useState(EMPTY_MEDIA);
  const [playError, setPlayError] = useState('');
  useEffect(() => {
    if (nearby || !container.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setNearby(true); observer.disconnect(); }
    }, { rootMargin: '200px' });
    observer.observe(container.current);
    return () => observer.disconnect();
  }, [nearby]);
  useEffect(() => {
    let mounted = true;
    setMedia(EMPTY_MEDIA);
    setPlayError('');
    if (!path || !nearby) return undefined;
    setState({ path, url: null, loading: true, failed: false });
    resolveStorageUrl(path).then((url) => {
      if (mounted) setState({ path, url, loading: false, failed: !url });
    }).catch(() => {
      if (mounted) setState({ path, url: null, loading: false, failed: true });
    });
    return () => { mounted = false; };
  }, [nearby, path]);
  useEffect(() => {
    const element = player.current;
    return () => {
      stop(element);
      if (activePlayer === element) activePlayer = null;
    };
  }, [state.url, path]);

  async function togglePlayback() {
    const element = player.current;
    if (!element) return;
    setPlayError('');
    if (!element.paused) { element.pause(); return; }
    try {
      await element.play();
    } catch {
      if (player.current === element) {
        setMedia((current) => ({ ...current, playing: false, buffering: false }));
        setPlayError('Preview could not play. Try again.');
      }
    }
  }
  function updateDuration(event) {
    const duration = event.currentTarget.duration;
    setMedia((current) => ({ ...current, duration: Number.isFinite(duration) && duration > 0 ? duration : 0 }));
  }
  function seek(event) {
    const element = player.current;
    if (!element || !media.duration) return;
    const time = Math.min(media.duration, Math.max(0, Number(event.target.value)));
    element.currentTime = time;
    setMedia((current) => ({ ...current, time }));
  }
  function toggleMute() {
    const element = player.current;
    if (!element) return;
    element.muted = !element.muted;
    setMedia((current) => ({ ...current, muted: element.muted }));
  }
  const progress = media.duration ? Math.min(100, Math.max(0, media.time / media.duration * 100)) : 0;
  return (
    <div className="sound-preview" ref={container}>
      {!path ? <p>Preview unavailable.</p> :
        !nearby || state.path !== path || state.loading ? <p role="status">Loading preview...</p> : (
          <>
            {state.url ? <div className="preview-player" role="group" aria-label={'Sound preview: ' + product.title}>
              <audio
                ref={player} hidden controlsList="nodownload" preload="none" src={state.url}
                onContextMenu={(event) => event.preventDefault()}
                aria-label={'Listen to ' + product.title}
                onLoadedMetadata={updateDuration} onDurationChange={updateDuration}
                onTimeUpdate={(event) => {
                  const time = event.currentTarget.currentTime;
                  setMedia((current) => ({ ...current, time: Number.isFinite(time) ? time : 0 }));
                }}
                onPlay={(event) => {
                  if (activePlayer !== event.currentTarget) stop(activePlayer);
                  activePlayer = event.currentTarget;
                  setMedia((current) => ({ ...current, playing: true }));
                }}
                onPause={() => setMedia((current) => ({ ...current, playing: false, buffering: false }))}
                onWaiting={() => setMedia((current) => ({ ...current, buffering: true }))}
                onPlaying={() => setMedia((current) => ({ ...current, playing: true, buffering: false }))}
                onEnded={(event) => {
                  event.currentTarget.currentTime = 0;
                  if (activePlayer === event.currentTarget) activePlayer = null;
                  setMedia((current) => ({ ...current, time: 0, playing: false, buffering: false }));
                }}
                onError={() => {
                  setState((current) => ({ ...current, failed: true }));
                  setMedia((current) => ({ ...current, playing: false, buffering: false }));
                }}
              />
              <button className="preview-control preview-play" type="button" disabled={state.failed}
                aria-label={(media.playing ? 'Pause' : 'Play') + ' preview of ' + product.title}
                onClick={togglePlayback}>
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  {media.playing ? <path d="M6 4h4v16H6zm8 0h4v16h-4z" /> : <path d="M7 4v16l14-8z" />}
                </svg>
              </button>
              <div className="preview-timeline">
                <input className="preview-seek" type="range" min="0" max={media.duration || 0} step="0.1"
                  value={Math.min(media.time, media.duration || 0)} disabled={!media.duration || state.failed}
                  aria-label={'Seek preview of ' + product.title}
                  aria-valuetext={formatTime(media.time) + ' of ' + formatTime(media.duration)}
                  style={{ '--preview-progress': progress + '%' }} onChange={seek} />
                <span className="preview-time" aria-hidden="true">{formatTime(media.time)} / {formatTime(media.duration)}</span>
              </div>
              <button className="preview-control preview-mute" type="button" disabled={state.failed}
                aria-label={(media.muted ? 'Unmute' : 'Mute') + ' preview of ' + product.title} onClick={toggleMute}>
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M3 9v6h4l5 5V4L7 9z" />
                  {media.muted ? <path d="m16 8-1.5 1.5L17 12l-2.5 2.5L16 16l2.5-2.5L21 16l1.5-1.5L20 12l2.5-2.5L21 8l-2.5 2.5z" /> :
                    <path d="M15 8a5 5 0 0 1 0 8v-2a3 3 0 0 0 0-4zm2-4a10 10 0 0 1 0 16v-2.5a7 7 0 0 0 0-11z" />}
                </svg>
              </button>
              {media.buffering && !state.failed ? <p className="preview-status" role="status">Buffering preview...</p> : null}
              {playError && !state.failed ? <p className="preview-status" role="status">{playError}</p> : null}
            </div> : null}
            {state.failed ? <p role="status">Preview unavailable. Please try again later.</p> : null}
          </>
        )}
    </div>
  );
}
